import {
  Box,
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Switch,
  Select,
  Button,
  useToast,
  Text,
  HStack,
  useColorMode,
  useColorModeValue,
  SimpleGrid,
  Icon,
  Divider,
  Tooltip,
  Avatar,
  Flex,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Center,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToken,
} from '@chakra-ui/react';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/apiClient';
import { colors } from '../assets/styles/colors';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import {
  FiMoon,
  FiSun,
  FiGlobe,
  FiTarget,
  FiBell,
  FiMail,
  FiLogOut,
  FiTrash2,
  FiCheckCircle,
  FiSettings,
  FiSliders,
  FiAlertTriangle,
  FiSave,
  FiFlag,
  FiVolume2,
  FiMessageSquare,
  FiUser,
} from 'react-icons/fi';
import { keyframes } from '@emotion/react';
import logger from '../utils/logger';

interface UserSettings {
  darkMode: boolean;
  targetLanguage: string;
  nativeLanguage: string;
  dailyGoal: number;
  notifications: boolean;
}

const Settings = () => {
  // Auth hooks
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const { applyUserLanguage } = useLanguage();

  // UI hooks
  const toast = useToast();
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [primary500] = useToken('colors', ['brand.500']);

  // Animation keyframes
  const floatAnimation = keyframes`
    0% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
    100% { transform: translateY(0); }
  `;

  // Color mode values
  const cardBg = useColorModeValue('white', colors.primary[700]);
  const borderColor = useColorModeValue('gray.200', colors.primary[600]);
  const hoverBg = useColorModeValue('gray.50', colors.primary[600]);
  const textColor = useColorModeValue(colors.primary[800], 'gray.100');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const iconColor = useColorModeValue(colors.primary[500], colors.primary[300]);
  const dangerBg = useColorModeValue('red.50', 'red.900');
  const cardShadow = useColorModeValue('0px 4px 12px rgba(0, 0, 0, 0.05)', '0px 4px 12px rgba(0, 0, 0, 0.2)');
  const hoverCardShadow = useColorModeValue('0px 8px 16px rgba(0, 0, 0, 0.1)', '0px 8px 16px rgba(0, 0, 0, 0.3)');

  // Common card styles
  const cardStyles = {
    bg: cardBg,
    borderWidth: "1px",
    borderColor: borderColor,
    borderRadius: "xl",
    overflow: "hidden",
    transition: "all 0.3s ease",
    boxShadow: cardShadow,
    _hover: {
      transform: 'translateY(-4px)',
      boxShadow: hoverCardShadow,
    }
  };

  // State hooks
  const [settings, setSettings] = useState<UserSettings>({
    darkMode: colorMode === 'dark',
    targetLanguage: 'english',
    nativeLanguage: 'english',
    dailyGoal: 10,
    notifications: true,
  });

  // Update settings.darkMode when colorMode changes externally (e.g., from navbar)
  useEffect(() => {
    setSettings(prevSettings => ({
      ...prevSettings,
      darkMode: colorMode === 'dark'
    }));
  }, [colorMode]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) return;

      try {
        const data = await apiClient.getUserSettings();

        if (data && data.settings) {
          // Ensure darkMode is synced with current colorMode
          const syncedSettings = {
            ...data.settings,
            darkMode: colorMode === 'dark'
          };
          setSettings(syncedSettings);

          // Set the interface language based on user's native language
          if (syncedSettings.nativeLanguage) {
            applyUserLanguage(syncedSettings.nativeLanguage);
          }
        }
      } catch (error) {
        logger.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, [user, colorMode, applyUserLanguage]);

  const handleSettingChange = async (key: keyof UserSettings, value: boolean | string | number) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    // Update interface language if native language is changed
    if (key === 'nativeLanguage' && typeof value === 'string') {
      applyUserLanguage(value);
    }

    if (user) {
      try {
        await apiClient.upsertUserSettings({ settings: newSettings });

        toast({
          title: t('settings.settingsSaved'),
          status: 'success',
          duration: 3000,
          isClosable: true,
          icon: <Icon as={FiCheckCircle} />,
        });
      } catch (error) {
        toast({
          title: t('settings.errorSaving'),
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    try {
      // TODO: Implement account deletion endpoint in the API
      // await apiClient.deleteAccount();

      toast({
        title: t('settings.accountDeleted'),
        description: t('settings.accountDeletedSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: t('settings.errorDeletingAccount'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      toast({
        title: t('settings.errorSigningOut'),
        description: error instanceof Error ? error.message : t('common.error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Container maxW={{ base: "container.sm", md: "container.lg" }} py={8} px={{ base: 4, md: 8 }}>
              <Heading
        as="h1"
        display="flex"
        alignItems="center"
        bgGradient="linear(to-l, #7928CA, #FF0080)"
                bgClip="text"
        fontWeight="extrabold"
        fontSize={{ base: "2xl", md: "3xl" }}
        mb={{ base: 6, md: 8 }}
        textAlign="left"
              >
        <Icon as={FiSettings} mr={2} />
                {t('common.settings')}
      </Heading>

      <VStack spacing={8} align="stretch">
        {/* User info card */}
          {user && (
          <Card
            {...cardStyles}
            mb={4}
          >
            <CardBody p={5}>
              <HStack spacing={4}>
              <Avatar
                size="md"
                name={user.email || ''}
                bg={`linear-gradient(135deg, ${colors.accent.blue}, ${colors.accent.purple})`}
                color="white"
              />
              <Box>
                <Text fontWeight="medium">{user.email}</Text>
                <Text fontSize="sm" color={mutedTextColor}>{t('settings.accountSettings')}</Text>
              </Box>
            </HStack>
            </CardBody>
          </Card>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {/* Left Column */}
          <VStack spacing={6} align="stretch">
            <Card
              {...cardStyles}
              transition="all 0.3s"
              _hover={{
                transform: 'translateY(-8px)',
                boxShadow: 'xl',
              }}
            >
              <CardHeader pb={2}>
                <HStack spacing={3}>
                  <Box
                    bg={useColorModeValue(colors.accent.blue + '20', colors.accent.blue + '30')}
                    p={2}
                    borderRadius="md"
                    color={colors.accent.blue}
                  >
                    <Icon as={colorMode === 'dark' ? FiMoon : FiSun} boxSize={5} />
                  </Box>
                  <Heading size="md">{t('settings.appearance')}</Heading>
                </HStack>
              </CardHeader>
              <CardBody pt={3}>
                <FormControl
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bg={hoverBg}
                  p={4}
                  borderRadius="lg"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-2px)', shadow: 'md', borderWidth: '1px', borderColor: colors.accent.blue }}
                >
                  <HStack>
                    <FormLabel mb="0" htmlFor="dark-mode-switch" fontWeight="medium">{t('settings.darkMode')}</FormLabel>
                    <Tooltip hasArrow label={colorMode === 'dark' ? t('settings.switchToLight') : t('settings.switchToDark')}>
                      <Box>
                        <Icon as={colorMode === 'dark' ? FiMoon : FiSun} color={iconColor} />
                      </Box>
                    </Tooltip>
                  </HStack>
                  <Switch
                    id="dark-mode-switch"
                    colorScheme="brand"
                    size="lg"
                    isChecked={settings.darkMode}
                    onChange={(e) => {
                      handleSettingChange('darkMode', e.target.checked);
                      toggleColorMode();
                    }}
                  />
                </FormControl>
              </CardBody>
            </Card>

            <Card
              {...cardStyles}
              transition="all 0.3s"
              _hover={{
                transform: 'translateY(-8px)',
                boxShadow: 'xl',
              }}
            >
              <CardHeader pb={2}>
                <HStack spacing={3}>
                  <Box
                    bg={useColorModeValue(colors.accent.green + '20', colors.accent.green + '30')}
                    p={2}
                    borderRadius="md"
                    color={colors.accent.green}
                  >
                    <Icon as={FiGlobe} boxSize={5} />
                  </Box>
                  <Heading size="md">{t('settings.language')}</Heading>
                </HStack>
              </CardHeader>
              <CardBody pt={3}>
                <VStack spacing={5} align="stretch">
                  <FormControl
                    p={4}
                    bg={hoverBg}
                    borderRadius="lg"
                    transition="all 0.3s"
                    _hover={{ transform: 'translateY(-2px)', shadow: 'md', borderWidth: '1px', borderColor: colors.accent.green }}
                  >
                    <HStack mb={3} spacing={2}>
                      <FormLabel mb="0" fontWeight="medium">{t('settings.targetLanguage')}</FormLabel>
                      <Tooltip hasArrow label={t('settings.targetLanguageTooltip')}>
                        <Box>
                          <Icon as={FiTarget} color={iconColor} />
                        </Box>
                      </Tooltip>
                    </HStack>
                    <Flex alignItems="center" gap={3}>
                      <Select
                        value={settings.targetLanguage}
                        onChange={(e) => handleSettingChange('targetLanguage', e.target.value)}
                        bg={cardBg}
                        borderColor={borderColor}
                        size="lg"
                        borderRadius="md"
                        _hover={{ borderColor: colors.primary[400] }}
                        _focus={{ borderColor: colors.primary[500], boxShadow: `0 0 0 1px ${colors.primary[500]}` }}
                        flex="1"
                      >
                        <option value="english">English</option>
                        <option value="hebrew">Hebrew</option>
                        <option value="dutch">Dutch</option>
                      </Select>
                      {settings.targetLanguage && (
                        <Box
                          p={2}
                          borderRadius="md"
                          bg={colors.primary[50]}
                          color={colors.primary[500]}
                        >
                          <Icon as={FiFlag} boxSize={6} />
                        </Box>
                      )}
                    </Flex>
                  </FormControl>

                  <FormControl
                    p={4}
                    bg={hoverBg}
                    borderRadius="lg"
                    transition="all 0.3s"
                    _hover={{ transform: 'translateY(-2px)', shadow: 'md', borderWidth: '1px', borderColor: colors.accent.green }}
                  >
                    <HStack mb={3} spacing={2}>
                      <FormLabel mb="0" fontWeight="medium">{t('settings.nativeLanguage')}</FormLabel>
                      <Tooltip hasArrow label={t('settings.nativeLanguageTooltip')}>
                        <Box>
                          <Icon as={FiGlobe} color={iconColor} />
                        </Box>
                      </Tooltip>
                    </HStack>
                    <Flex alignItems="center" gap={3}>
                      <Select
                        value={settings.nativeLanguage}
                        onChange={(e) => handleSettingChange('nativeLanguage', e.target.value)}
                        bg={cardBg}
                        borderColor={borderColor}
                        size="lg"
                        borderRadius="md"
                        _hover={{ borderColor: colors.primary[400] }}
                        _focus={{ borderColor: colors.primary[500], boxShadow: `0 0 0 1px ${colors.primary[500]}` }}
                        flex="1"
                      >
                        <option value="english">English</option>
                        <option value="hebrew">Hebrew</option>
                        <option value="dutch">Dutch</option>
                      </Select>
                      {settings.nativeLanguage && (
                        <Box
                          p={2}
                          borderRadius="md"
                          bg={colors.primary[50]}
                          color={colors.primary[500]}
                        >
                          <Icon as={FiMessageSquare} boxSize={6} />
                        </Box>
                      )}
                    </Flex>
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
          </VStack>

          {/* Right Column */}
          <VStack spacing={6} align="stretch">
            <Card
              {...cardStyles}
              transition="all 0.3s"
              _hover={{
                transform: 'translateY(-8px)',
                boxShadow: 'xl',
              }}
            >
              <CardHeader pb={2}>
                <HStack spacing={3}>
                  <Box
                    bg={useColorModeValue(colors.accent.yellow + '20', colors.accent.yellow + '30')}
                    p={2}
                    borderRadius="md"
                    color={colors.accent.yellow}
                  >
                    <Icon as={FiTarget} boxSize={5} />
                  </Box>
                  <Heading size="md">{t('dashboard.dailyGoal')}</Heading>
                </HStack>
              </CardHeader>
              <CardBody pt={3}>
                <FormControl
                  p={4}
                  bg={hoverBg}
                  borderRadius="lg"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-2px)', shadow: 'md', borderWidth: '1px', borderColor: colors.accent.yellow }}
                >
                  <HStack mb={3} spacing={2} justify="space-between">
                    <FormLabel mb="0" fontWeight="medium">{t('settings.dailyGoalSentences')}</FormLabel>
                    <Badge
                      colorScheme="green"
                      fontSize="md"
                      py={1}
                      px={3}
                      borderRadius="full"
                      bg={colors.accent.green + '40'}
                      color={useColorModeValue('green.700', 'green.200')}
                    >
                      {settings.dailyGoal}
                    </Badge>
                  </HStack>

                  <Box pt={6} pb={2}>
                    <Slider
                      id="daily-goal-slider"
                      min={1}
                      max={30}
                      step={1}
                      value={settings.dailyGoal}
                      onChange={(val) => handleSettingChange('dailyGoal', val)}
                      colorScheme="yellow"
                    >
                      <SliderMark value={5} mt={3} ml={-2.5} fontSize="xs" color={mutedTextColor}>
                        5
                      </SliderMark>
                      <SliderMark value={15} mt={3} ml={-2.5} fontSize="xs" color={mutedTextColor}>
                        15
                      </SliderMark>
                      <SliderMark value={25} mt={3} ml={-2.5} fontSize="xs" color={mutedTextColor}>
                        25
                      </SliderMark>
                      <SliderTrack bg={colors.accent.yellow + '30'}>
                        <SliderFilledTrack bg={colors.accent.yellow} />
                      </SliderTrack>
                      <SliderThumb
                        boxSize={6}
                        bg={colors.accent.yellow}
                        _focus={{
                          boxShadow: `0 0 0 3px ${colors.accent.yellow}40`
                        }}
                      >
                        <Box color="white" as={FiTarget} />
                      </SliderThumb>
                    </Slider>
                  </Box>
                </FormControl>
              </CardBody>
            </Card>

            <Card
              {...cardStyles}
              transition="all 0.3s"
              _hover={{
                transform: 'translateY(-8px)',
                boxShadow: 'xl',
              }}
            >
              <CardHeader pb={2}>
                <HStack spacing={3}>
                  <Box
                    bg={useColorModeValue(colors.accent.purple + '20', colors.accent.purple + '30')}
                    p={2}
                    borderRadius="md"
                    color={colors.accent.purple}
                  >
                    <Icon as={FiBell} boxSize={5} />
                  </Box>
                  <Heading size="md">{t('settings.notifications')}</Heading>
                </HStack>
              </CardHeader>
              <CardBody pt={3}>
                <FormControl
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bg={hoverBg}
                  p={4}
                  borderRadius="lg"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-2px)', shadow: 'md', borderWidth: '1px', borderColor: colors.accent.purple }}
                >
                  <HStack>
                    <FormLabel mb="0" htmlFor="notifications-switch" fontWeight="medium">
                      {t('settings.enableNotifications')}
                    </FormLabel>
                    <Tooltip hasArrow label={t('settings.notificationsTooltip')}>
                      <Box>
                        <Icon as={FiBell} color={iconColor} />
                      </Box>
                    </Tooltip>
                  </HStack>
                  <Switch
                    id="notifications-switch"
                    colorScheme="purple"
                    size="lg"
                    isChecked={settings.notifications}
                    onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                  />
                </FormControl>
              </CardBody>
            </Card>

            <Card
              {...cardStyles}
              transition="all 0.3s"
              _hover={{
                transform: 'translateY(-8px)',
                boxShadow: 'xl',
              }}
            >
              <CardHeader pb={2}>
                <HStack spacing={3}>
                  <Box
                    bg={useColorModeValue(colors.primary[100], colors.primary[600])}
                    p={2}
                    borderRadius="md"
                    color={useColorModeValue(colors.primary[600], colors.primary[200])}
                  >
                    <Icon as={FiUser} boxSize={5} />
                  </Box>
                  <Heading size="md">{t('settings.account')}</Heading>
                </HStack>
              </CardHeader>
              <CardBody pt={3}>
                <VStack spacing={5} align="stretch">
                  <HStack
                    p={4}
                    bg={hoverBg}
                    borderRadius="lg"
                    spacing={3}
                    justify="space-between"
                    transition="all 0.3s"
                    _hover={{ transform: 'translateY(-2px)', shadow: 'sm', borderWidth: '1px', borderColor: colors.primary[400] }}
                  >
                    <HStack>
                      <Icon as={FiMail} color={iconColor} boxSize={5} />
                      <Text fontWeight="medium">{t('auth.email')}</Text>
                    </HStack>
                    <Text color={mutedTextColor}>{user?.email}</Text>
                  </HStack>

                  <Button
                    leftIcon={<FiLogOut />}
                    colorScheme="brand"
                    variant="outline"
                    size="lg"
                    w="100%"
                    onClick={handleSignOut}
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'sm',
                      bg: useColorModeValue(colors.primary[50], colors.primary[600]),
                    }}
                    transition="all 0.3s"
                  >
                    {t('auth.signOut')}
                  </Button>

                  {/* Danger Zone */}
                  <Box
                    mt={4}
                    p={4}
                    bg={useColorModeValue('red.50', 'rgba(254, 178, 178, 0.12)')}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={useColorModeValue('red.200', 'red.700')}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-2px)',
                      shadow: 'md',
                      borderColor: 'red.500',
                    }}
                  >
                    <VStack align="stretch" spacing={3}>
                      <HStack spacing={2}>
                        <Icon as={FiAlertTriangle} color="red.500" />
                        <Heading size="sm" color="red.500">{t('settings.dangerZone')}</Heading>
                      </HStack>
                      <Text fontSize="sm" color={mutedTextColor}>
                        {t('settings.deleteAccountWarning')}
                      </Text>
                      <Button
                        leftIcon={<FiTrash2 />}
                        colorScheme="red"
                        variant="outline"
                        size="md"
                        onClick={onOpen}
                        _hover={{
                          transform: 'translateY(-2px)',
                          boxShadow: 'sm',
                          bg: 'red.50',
                        }}
                        transition="all 0.3s"
                      >
                        {t('settings.deleteAccount')}
                      </Button>
                    </VStack>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </SimpleGrid>
      </VStack>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" borderBottomWidth="1px" borderColor={borderColor}>
              <HStack>
                <Icon as={FiAlertTriangle} color="red.500" mr={2} />
                {t('settings.deleteAccount')}
              </HStack>
            </AlertDialogHeader>

            <AlertDialogBody py={4}>
              <VStack align="stretch" spacing={4}>
                <Box
                  bg={useColorModeValue("red.50", "rgba(254, 178, 178, 0.12)")}
                  p={3}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={useColorModeValue("red.100", "red.700")}
                >
                  <HStack>
                    <Icon as={FiAlertTriangle} color="red.500" boxSize={6} />
                    <Text fontWeight="bold" color="red.500">{t('common.warning')}</Text>
                  </HStack>
                </Box>
                <Text>{t('settings.confirmDeleteAccount')}</Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter borderTopWidth="1px" borderColor={borderColor}>
              <Button ref={cancelRef} onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteAccount}
                ml={3}
                leftIcon={<FiTrash2 />}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'sm',
                }}
                transition="all 0.3s"
              >
                {t('settings.deleteAccount')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default Settings;
